import { Capacitor } from '@capacitor/core'
import { nextTick } from 'vue'

import { useDialog } from '@last/core-ui/paprika'

import { t } from '@/i18n'
import { useConfigStore } from '@/store/config'
import type { Config } from '@/types/config'

import { AdyenDataphone } from './adyenDataphone'
import { DevDataphone } from './devDataphone'
import { IntegratorDataphone } from './integratorDataphone'
import { RedsysDataphone } from './redsysDataphone'

export interface IDataphone {
  initialized: boolean
  canCancel: boolean
  init: () => void
  charge: (args: {
    amount: number
    tip: number
    paymentId: string
    billId: string
    tabId: string
  }) => Promise<any>
  refund: (payment: any, tabId: string) => Promise<any>
  cancel: (args: { paymentId: string; tabId: string }) => Promise<any>
}

class Dataphone {
  private device?: IDataphone
  private dialogInstance: any

  async init(config: Config, preferredDataphoneId?: string | null) {
    try {
      const platform = Capacitor.getPlatform()
      const configStore = useConfigStore()
      if (
        !configStore.config.paymentMethods.includes('Dataphone') &&
        config.terminals?.length === 0
      )
        return

      if (platform === 'web' && config.terminals.length === 0) {
        this.device = new DevDataphone()
      } else if (configStore.currentAdyenDataphone) {
        this.device = new AdyenDataphone(configStore.currentAdyenDataphone)
      } else {
        for (const dataphone of config.dataphoneConfigs) {
          if (
            dataphone.id === preferredDataphoneId ||
            (!preferredDataphoneId && dataphone.business)
          ) {
            this.device = this.device ?? new RedsysDataphone(dataphone)
            break
          }
        }

        for (const dataphone of config.adyenDataphones) {
          if (dataphone.id === preferredDataphoneId) {
            this.device = this.device ?? new AdyenDataphone(dataphone)
            break
          }
        }

        for (const dataphone of config.terminals) {
          if (dataphone.terminalId === preferredDataphoneId) {
            this.device = this.device ?? new IntegratorDataphone(dataphone)
            break
          }
        }
      }

      if (!this.device) return
      this.device.init()
    } catch {
      throw new Error('No dataphone capability')
    }
  }

  async charge({
    amount,
    tip,
    paymentId,
    billId,
    tabId
  }: {
    amount: number
    tip: number
    paymentId: string
    billId: string
    tabId: string
  }) {
    if (!this.device || !this.initialized()) {
      throw new Error(t('checkout.dataphone.not-initialized'))
    }
    try {
      let cancelled = false
      const dialog = useDialog()
      this.dialogInstance = dialog({
        icon: 'status',
        iconAnimation: 'animate-spin',
        ...(this.device.canCancel
          ? {
              mainLabel: t('payment-screen.cancel-operation'),
              onConfirm: () => {
                cancelled = true
              }
            }
          : {
              noLabels: true
            }),
        title: t('payment-screen.payment-in-process'),
        content: t('payment-screen.check-the-dataphone'),
        dismissable: false,
        hiddenClose: true
      })
      const metadata = await this.device.charge({ amount, tip, paymentId, billId, tabId })
      if (cancelled) {
        this.device.cancel({ paymentId, tabId })
        throw new Error('PAYMENT_CANCELLED')
      }
      await nextTick()
      this.dialogInstance?.close()
      return metadata
    } catch (e) {
      this.dialogInstance?.close()
      throw e
    }
  }

  refund(payment: any, tabId: string) {
    if (!this.device || !this.initialized()) {
      throw new Error(t('checkout.dataphone.not-initialized'))
    }
    return this.device.refund(payment, tabId)
  }

  initialized() {
    return this.device?.initialized ?? false
  }
}

export default new Dataphone()
